import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace, { jobTab } from "@/components/job-workspace";
import JobAssignment from "@/components/job-assignment";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import JobProgress from "./progress";
import RescheduleDetails from "../reschedules/details";
import type { Offer } from "../reschedules/types";
import {
  newLocationRecordingOperationalNotificationPath,
  newLocationJobReschedulePath,
  locationJobRescheduleExpiryPath,
  locationJobPath,
  locationVehicleHistoryPath,
  locationJobBillingReviewPath,
  newLocationJobBillingReviewPath,
} from "@/routes";
import { dateTime, money, type Job } from "./types";

type Pagination = { page: number; pages: number };
export default function JobShow({
  job,
  billing_reviews,
  billing_reviews_pagination,
  warnings,
  reschedules,
  reschedulable,
  email_action_required,
  billing_staff,
  billing_assignee_name,
  reschedules_pagination,
}: {
  job: Job;
  billing_reviews_pagination: Pagination;
  billing_reviews: { id: string; state: string }[];
  billing_staff: { id: string; name: string }[];
  billing_assignee_name: string | null;
  reschedules_pagination: Pagination;
  email_action_required: boolean;
  reschedules: (Offer & { viewing_url: string })[];
  reschedulable: boolean;
  warnings: { bay_name: string; starts_at: string; ends_at: string }[];
}) {
  const {
    url,
    props: { auth, i18n },
  } = usePage();
  const { t } = useTranslation();
  if (!auth.location) return null;
  const id = auth.location.id,
    section = jobTab(url),
    { customer, vehicle, total } = job.representation;
  const continuation = (key: string, value: number) =>
    locationJobPath(id, job.id, {
      section: section === "overview" ? undefined : section,
      [key]: value,
    });
  const pagination = (pages: Pagination, key: string, label: string) =>
    pages.pages > 1 && (
      <nav className="flex flex-wrap gap-4" aria-label={label}>
        {pages.page > 1 && (
          <Link className="underline" href={continuation(key, pages.page - 1)}>
            {t("customers.previous")}
          </Link>
        )}
        {pages.page < pages.pages && (
          <Link className="underline" href={continuation(key, pages.page + 1)}>
            {t("customers.next")}
          </Link>
        )}
      </nav>
    );
  return (
    <JobWorkspace>
      <Head title={`${vehicle.make} ${vehicle.model}`} />
      <div className="space-y-section">
        <h2 className="text-section font-semibold">
          {t(`workspace.tabs.${section}`)}
        </h2>
        {email_action_required && (
          <p role="alert">{t("notifications.action_required")}</p>
        )}
        {job.advance_overdue && <p role="alert">{t("payments.overdue")}</p>}
        {section !== "billing" && (
          <>
            {warnings.length > 0 && (
              <div role="status" className="space-y-2 rounded-lg border p-4">
                <h3 className="font-medium">{t("jobs.overlap")}</h3>
                <p className="text-muted-foreground text-sm">
                  {t("jobs.overlap_help")}
                </p>
                <ul>
                  {warnings.map((warning, index) => (
                    <li key={index}>
                      {warning.bay_name} ·{" "}
                      {dateTime(warning.starts_at, job.time_zone, i18n.locale)}{" "}
                      – {dateTime(warning.ends_at, job.time_zone, i18n.locale)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Card>
              <CardHeader>
                <CardTitle>{t("jobs.booking")}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="font-medium break-words">{customer.name}</p>
                  <p className="break-words">
                    {[customer.email, customer.phone]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <p>
                    {[vehicle.fabrication_year, vehicle.registration_number]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <Link
                    className="inline-flex min-h-11 items-center underline"
                    href={locationVehicleHistoryPath(id, job.vehicle_id)}
                  >
                    {t("vehicle_reports.open")}
                  </Link>
                </div>
                <div className="space-y-2">
                  <p>{job.bay_name}</p>
                  <p>
                    {dateTime(job.starts_at, job.time_zone, i18n.locale)} –{" "}
                    {dateTime(job.ends_at, job.time_zone, i18n.locale)}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {job.time_zone}
                  </p>
                  <p>
                    {job.current_staff.map((person) => person.name).join(", ")}
                  </p>
                  {section === "schedule" && <JobAssignment />}
                </div>
              </CardContent>
            </Card>
          </>
        )}
        {section === "overview" && (
          <JobProgress
            job={job}
            billingStaff={billing_staff}
            billingAssigneeName={billing_assignee_name}
          />
        )}
        {section !== "schedule" && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>{t("jobs.services")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {total.lines.map((line) => (
                  <div key={line.line_id} className="space-y-1 border-b pb-4">
                    <p className="font-medium break-words">
                      {line.name} × {line.quantity}
                    </p>
                    {line.description && (
                      <p className="text-muted-foreground text-sm break-words">
                        {line.description}
                      </p>
                    )}
                    <p>
                      {money(line.total_minor, total.currency, i18n.locale)}
                    </p>
                    {line.tax_label && (
                      <p className="text-muted-foreground text-sm">
                        {line.tax_label}
                      </p>
                    )}
                  </div>
                ))}
                <dl className="space-y-3">
                  {(["net_minor", "tax_minor", "total_minor"] as const).map(
                    (field) => (
                      <div
                        key={field}
                        className="flex flex-wrap justify-between gap-2"
                      >
                        <dt>{t(`jobs.${field}`)}</dt>
                        <dd className="font-medium">
                          {money(total[field], total.currency, i18n.locale)}
                        </dd>
                      </div>
                    ),
                  )}
                </dl>
                <p className="text-muted-foreground text-sm">
                  {t("jobs.terms_help")}
                </p>
              </CardContent>
            </Card>
            {(billing_reviews.length > 0 || job.work_state === "billing") && (
              <section className="space-y-3">
                <h3 className="text-lg font-semibold">
                  {t("billing_reviews.title")}
                </h3>
                {job.work_state === "billing" && (
                  <Button
                    variant="outline"
                    render={
                      <Link
                        href={newLocationJobBillingReviewPath(id, job.id)}
                      />
                    }
                  >
                    {t("billing_reviews.prepare")}
                  </Button>
                )}
                {billing_reviews.map((review) => (
                  <Link
                    className="block min-h-11 py-2 underline"
                    key={review.id}
                    href={locationJobBillingReviewPath(id, job.id, review.id)}
                  >
                    {t(`billing_reviews.states.${review.state}`)}
                  </Link>
                ))}
                {pagination(
                  billing_reviews_pagination,
                  "reviews_page",
                  t("billing_reviews.title"),
                )}
              </section>
            )}
            {section === "billing" && (
              <JobProgress
                job={job}
                billingStaff={billing_staff}
                billingAssigneeName={billing_assignee_name}
              />
            )}
          </>
        )}
        {section !== "billing" && (
          <section className="space-y-4">
            <h3 className="text-lg font-semibold">{t("reschedules.title")}</h3>
            {reschedulable &&
              !reschedules.some((offer) => offer.state === "pending") && (
                <Button
                  variant="outline"
                  render={
                    <Link href={newLocationJobReschedulePath(id, job.id)} />
                  }
                >
                  {t("reschedules.title")}
                </Button>
              )}
            {reschedules
              .filter(
                (offer) =>
                  section === "schedule" ||
                  offer.state === "pending" ||
                  offer.closable,
              )
              .map((offer) => (
                <details
                  key={offer.id}
                  className="rounded-lg border p-4"
                  open={offer.state === "pending"}
                >
                  <summary className="cursor-pointer font-medium">
                    {t(`reschedules.${offer.state}_status`)}
                  </summary>
                  <div className="mt-4 space-y-4">
                    <RescheduleDetails offer={offer} />
                    {offer.state === "pending" && (
                      <Button
                        variant="outline"
                        render={
                          <Link
                            href={newLocationRecordingOperationalNotificationPath(
                              id,
                              offer.id,
                            )}
                          />
                        }
                      >
                        {t("notifications.preview")}
                      </Button>
                    )}
                    <Label htmlFor={`reschedule-link-${offer.id}`}>
                      {t("reschedules.link")}
                    </Label>
                    <Input
                      id={`reschedule-link-${offer.id}`}
                      readOnly
                      value={offer.viewing_url}
                      onFocus={(event) => event.target.select()}
                    />
                    <p className="text-muted-foreground text-sm">
                      {t("proposals.delivery_help")}
                    </p>
                    <a href={offer.viewing_url} className="underline">
                      {t("reschedules.open")}
                    </a>
                    {offer.closable && (
                      <Form
                        action={locationJobRescheduleExpiryPath(
                          id,
                          job.id,
                          offer.id,
                        )}
                        method="post"
                      >
                        {({ processing }) => (
                          <>
                            <input
                              type="hidden"
                              name="revision"
                              value={offer.revision}
                            />
                            <Button
                              type="submit"
                              variant="outline"
                              disabled={processing}
                            >
                              {t("reschedules.close_expired")}
                            </Button>
                          </>
                        )}
                      </Form>
                    )}
                  </div>
                </details>
              ))}
            {pagination(
              reschedules_pagination,
              "reschedules_page",
              t("reschedules.title"),
            )}
          </section>
        )}
      </div>
    </JobWorkspace>
  );
}
